import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading } from "../components/Layout";
import { ArrowRight, ArrowLeft, Sparkles, Check, Rocket, UserPlus, X, Mailbox, Trash2 } from "lucide-react";
import { STAGES, PROFILE_Q, PROCESS_Q, Field } from "./onboarding/wizardConfig";

export default function Onboarding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [milestone, setMilestone] = useState(0);   // 0..4
  const [qIdx, setQIdx] = useState(0);              // within milestone 0/1
  const [profile, setProfile] = useState({});
  const [process, setProcess] = useState({});
  const [mods, setMods] = useState([]);
  const [invite, setInvite] = useState({ name: "", email: "", role: "rep" });
  const [team, setTeam] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [anim, setAnim] = useState(true);
  const [showStageConfirm, setShowStageConfirm] = useState(false);
  const [approvalStages, setApprovalStages] = useState([]);
  const [review, setReview] = useState(null);

  useEffect(() => {
    api.get("/onboarding/wizard").then((r) => {
      const d = r.data;
      setState(d);
      const c = d.currency || {};
      setProfile({ reporting_currency: c.reporting_currency, transact_currencies: c.transact_currencies || [], region_currency_defaults: c.region_currency_defaults || {}, ...(d.business_profile || {}) });
      setProcess(d.sales_process || {});
      setMods(d.enabled_modules || []);
      const cs = d.completed ? 4 : Math.min((d.last_milestone ?? d.current_step) || 0, 4);
      setMilestone(cs);
    }).catch(() => toast.error("Could not load setup"));
  }, []);

  const transition = (fn) => { setAnim(false); setTimeout(() => { fn(); setAnim(true); }, 130); };
  // Review recap is sourced from the SERVER (persisted state + real pipeline_config + users),
  // not client state — so it faithfully shows what actually saved and surfaces any gap.
  useEffect(() => {
    if (milestone !== 4) return;
    Promise.all([
      api.get("/onboarding/wizard"),
      api.get("/pipeline/config").catch(() => ({ data: { labels: {}, approval_stages: [] } })),
      api.get("/users").catch(() => ({ data: [] })),
    ]).then(([w, p, u]) => setReview({ w: w.data, pipe: p.data, users: u.data || [] })).catch(() => {});
  }, [milestone]);
  const editJump = (m, opts = {}) => transition(() => { setReview(null); setMilestone(m); setQIdx(opts.qIdx ?? 0); setShowStageConfirm(!!opts.stage); });

  if (!state) return <div className="min-h-screen flex items-center justify-center bg-[#F5F3EE]"><Loading /></div>;

  const steps = state.steps;
  const domain = state.tenant?.sending_domain;

  const analyze = async () => {
    if (!profile.website) { transition(() => setQIdx((i) => i + 1)); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/onboarding/wizard/analyze-website", { url: profile.website });
      if (data.fetched && data.suggestions) {
        const s = data.suggestions;
        setProfile((p) => ({ ...p, business_model: p.business_model || s.business_model || "",
          icp_summary: s.icp_summary || p.icp_summary || "",
          compliance_context: p.compliance_context || s.suggested_compliance || "" }));
        toast.success("Site read — suggestions prefilled. Review and edit anything.");
      } else toast.info("Couldn't read that site — no problem, fill it in manually.");
    } catch { toast.info("Couldn't read that site — fill it in manually."); }
    finally { setAnalyzing(false); transition(() => setQIdx((i) => i + 1)); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/onboarding/wizard/business-profile", {
        ...profile, regulated: profile.regulated === "Yes",
        seat_estimate: profile.seat_estimate ? Number(profile.seat_estimate) : null,
        transact_currencies: profile.transact_currencies || [],
        region_currency_defaults: (profile.transact_currencies || []).length ? (profile.region_currency_defaults || {}) : {} });
      transition(() => { setMilestone(1); setQIdx(0); });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const saveProcess = async () => {
    setSaving(true);
    try { await api.put("/onboarding/wizard/sales-process", { ...process, approval_stage_ids: approvalStages });
      setShowStageConfirm(false); transition(() => { setMilestone(2); setQIdx(0); });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const stageLabels = () => {
    const raw = (process.pipeline_stage_names || "").trim();
    const names = raw ? raw.split(/→|->|\||,|\n|;/).map((s) => s.trim()).filter(Boolean) : [];
    return STAGES.map((s, i) => ({ id: s, label: names[i] || s }));
  };
  const saveModules = async () => {
    setSaving(true);
    try { const { data } = await api.put("/onboarding/wizard/modules", { enabled_modules: mods });
      setMods(data.enabled_modules); transition(() => setMilestone(4));
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const addTeammate = async () => {
    if (!invite.name.trim() || !invite.email.trim()) { toast.error("Name and email required"); return; }
    const used = (state.team_count || 0) + team.length;
    if (state.tenant?.seat_limit && used >= state.tenant.seat_limit) {
      toast.error(`Seat limit reached (${state.tenant.seat_limit}) — an admin can add seats later`); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding/wizard/team/invite", invite);
      setTeam((t) => [...t, { ...invite, id: data.id }]);
      setInvite({ name: "", email: "", role: "rep" });
      toast.success(data.welcome_email_mocked ? "Added (welcome email mocked in preview)" : "Teammate invited");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add teammate"); } finally { setSaving(false); }
  };
  const removeTeammate = async (i) => {
    const m = team[i];
    if (m.id) { try { await api.delete(`/users/${m.id}`); } catch { /* best effort */ } }
    setTeam((t) => t.filter((_, j) => j !== i));
    toast.success("Removed");
  };
  const saveDraft = (m) => { api.put("/onboarding/wizard/draft", { business_profile: profile, sales_process: process, last_milestone: m ?? milestone }).catch(() => {}); };

  const launch = async () => {
    setSaving(true);
    try { await api.post("/onboarding/wizard/complete"); toast.success("Workspace ready. Welcome aboard!");
      window.location.assign("/");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not finish"); } finally { setSaving(false); }
  };

  const toggleMod = (k) => setMods((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]));

  // ---- Milestone renderers ----
  const fieldCtx = { currency: state.currency, regions: state.regions || [], profile };
  const qList = (milestone === 0 ? PROFILE_Q : PROCESS_Q).filter((q) => !q.visible || q.visible(milestone === 0 ? profile : process, fieldCtx));
  const data = milestone === 0 ? profile : process;
  const setData = milestone === 0 ? setProfile : setProcess;

  const canNextQ = () => { const q = qList[qIdx]; const v = data[q.key]; return !q.required || (Array.isArray(v) ? v.length > 0 : (v && String(v).trim())); };
  const nextQ = () => {
    const q = qList[qIdx];
    if (q.type === "website" && milestone === 0) return analyze();
    if (!canNextQ()) { toast.error("This one's required"); return; }
    saveDraft();
    if (qIdx < qList.length - 1) transition(() => setQIdx((i) => i + 1));
    else if (milestone === 0) saveProfile();
    else transition(() => setShowStageConfirm(true));
  };
  const prevQ = () => {
    if (qIdx > 0) transition(() => setQIdx((i) => i - 1));
    else if (milestone > 0) transition(() => { setMilestone((m) => m - 1); setQIdx(0); });
  };

  return (
    <div className="min-h-screen flex bg-[#F5F3EE]" data-testid="onboarding-wizard">
      {/* Progress rail */}
      <aside className="w-72 shrink-0 bg-[#080808] px-8 py-10 hidden md:flex flex-col">
        <div className="font-mono text-[10px] tracking-[0.18em] text-white/40 uppercase">Workspace Setup</div>
        <div className="text-white text-xl font-light mt-1 mb-10">{state.tenant?.name || "Your workspace"}</div>
        <ol className="space-y-1">
          {steps.map((s, i) => {
            const done = i < milestone || state.completed;
            const active = i === milestone && !state.completed;
            return (
              <li key={s.key} data-testid={`wiz-milestone-${s.key}`}
                className={`flex items-center gap-3 py-2.5 text-sm transition-colors ${active ? "text-white" : done ? "text-white/55" : "text-white/30"}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-mono border ${done ? "bg-[#3E8F99] border-[#3E8F99] text-white" : active ? "border-white text-white" : "border-white/25"}`}>
                  {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                {s.label}
              </li>
            );
          })}
        </ol>
        <div className="mt-auto pt-8 font-mono text-[10px] text-white/30 leading-relaxed">
          Finishing setup won't change your subscription — activation stays a separate, deliberate step.
        </div>
      </aside>

      {/* Stage */}
      <main className="flex-1 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 md:px-16">
          <div className={`w-full max-w-2xl transition-all duration-150 ${anim ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>

            {(milestone === 0 || (milestone === 1 && !showStageConfirm)) && (() => {
              const q = qList[qIdx];
              return (
                <div>
                  <div className="font-mono text-[10px] tracking-[0.16em] text-[#3E8F99] uppercase mb-4">
                    {steps[milestone].label} · {qIdx + 1}/{qList.length}
                  </div>
                  <h1 className="text-3xl md:text-4xl font-light text-[#1C2530] leading-tight mb-3">{q.label}</h1>
                  <p className="text-base text-[#1C2530]/55 mb-8 max-w-xl">{q.helper}</p>
                  <Field q={q} value={data[q.key]} onChange={(v) => setData((d) => ({ ...d, [q.key]: v }))} onEnter={nextQ} ctx={fieldCtx} />
                  <div className="flex items-center gap-3 mt-12">
                    <button onClick={prevQ} data-testid="wiz-back" disabled={milestone === 0 && qIdx === 0}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#1C2530]/60 hover:text-[#1C2530] disabled:opacity-0 transition-colors">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button onClick={nextQ} data-testid="wiz-next" disabled={analyzing || saving}
                      className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-6 py-3 text-sm rounded-full hover:bg-[#2a3644] disabled:opacity-40 transition-colors">
                      {q.type === "website" ? (analyzing ? "Reading site…" : (data[q.key] ? <>Analyze & continue <Sparkles className="w-4 h-4" /></> : <>Skip <ArrowRight className="w-4 h-4" /></>))
                        : (saving ? "Saving…" : <>Continue <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </div>
              );
            })()}

            {milestone === 1 && showStageConfirm && (
              <div data-testid="wiz-stage-confirm">
                <div className="font-mono text-[10px] tracking-[0.16em] text-[#3E8F99] uppercase mb-4">Sales Process · Stage mapping</div>
                <h1 className="text-3xl md:text-4xl font-light text-[#1C2530] leading-tight mb-3">Here's how we mapped your stages</h1>
                <p className="text-base text-[#1C2530]/55 mb-8 max-w-xl">These become your board labels. Tick any stage that needs manager approval to enter — it uses your existing approval ladder, not a separate system.</p>
                <div className="space-y-1.5">
                  {stageLabels().map((s) => (
                    <div key={s.id} className="flex items-center justify-between bg-white border border-[#DFDAD0] px-4 py-2.5 rounded-sm">
                      <div className="text-sm text-[#1C2530]"><span className="font-medium">{s.label}</span>{s.label !== s.id && <span className="ml-2 font-mono text-[10px] text-[#1C2530]/40 uppercase">{s.id}</span>}</div>
                      <label className="flex items-center gap-1.5 text-xs text-[#1C2530]/60 cursor-pointer" data-testid={`wiz-approval-${s.id.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                        <input type="checkbox" checked={approvalStages.includes(s.id)} onChange={() => setApprovalStages((a) => a.includes(s.id) ? a.filter((x) => x !== s.id) : [...a, s.id])} /> Needs approval
                      </label>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-10">
                  <button onClick={() => transition(() => setShowStageConfirm(false))} data-testid="wiz-back" className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#1C2530]/60 hover:text-[#1C2530]"><ArrowLeft className="w-4 h-4" /> Back</button>
                  <button onClick={saveProcess} data-testid="wiz-stage-confirm-continue" disabled={saving} className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-6 py-3 text-sm rounded-full hover:bg-[#2a3644] disabled:opacity-40">{saving ? "Saving…" : <>Continue <ArrowRight className="w-4 h-4" /></>}</button>
                </div>
              </div>
            )}

            {milestone === 2 && (
              <div data-testid="wiz-team-step">
                <div className="font-mono text-[10px] tracking-[0.16em] text-[#3E8F99] uppercase mb-4">Team Setup</div>
                <h1 className="text-3xl md:text-4xl font-light text-[#1C2530] leading-tight mb-3">Bring your team in</h1>
                <p className="text-base text-[#1C2530]/55 mb-8 max-w-xl">
                  Add teammates now or later. Invites must use your workspace domain{domain ? <> <strong className="text-[#1C2530]">@{domain}</strong></> : ""}.
                </p>
                <div className="space-y-3 bg-white border border-[#DFDAD0] p-5 rounded-md">
                  <input data-testid="wiz-invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} placeholder="Full name" className="w-full border border-[#DFDAD0] px-3 py-2.5 text-sm outline-none focus:border-[#1C2530] rounded-sm" />
                  <input data-testid="wiz-invite-email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder={`name@${domain || "yourcompany.com"}`} className="w-full border border-[#DFDAD0] px-3 py-2.5 text-sm outline-none focus:border-[#1C2530] rounded-sm" />
                  <div className="flex gap-2">
                    <select data-testid="wiz-invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="flex-1 border border-[#DFDAD0] px-3 py-2.5 text-sm bg-white outline-none rounded-sm">
                      <option value="rep">Rep</option>
                      <option value="supervisor">Sales Lead</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button onClick={addTeammate} data-testid="wiz-invite-add" disabled={saving}
                      className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-5 py-2.5 text-sm rounded-sm hover:bg-[#2a3644] disabled:opacity-40">
                      <UserPlus className="w-4 h-4" /> Add
                    </button>
                  </div>
                </div>
                {team.length > 0 && (
                  <div className="mt-4 space-y-1.5" data-testid="wiz-team-list">
                    {team.map((t, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-sm text-[#1C2530]/70 bg-white border border-[#DFDAD0] px-3 py-2 rounded-sm">
                        <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#3E8F99]" /> {t.name} · {t.email} · {t.role}</span>
                        <button onClick={() => removeTeammate(i)} data-testid={`wiz-team-remove-${i}`} className="text-[#B23A2E] hover:bg-[#B23A2E]/10 p-1 rounded-sm"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-10">
                  <button onClick={() => transition(() => { setMilestone(1); setQIdx(PROCESS_Q.length - 1); })} data-testid="wiz-back" className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#1C2530]/60 hover:text-[#1C2530]"><ArrowLeft className="w-4 h-4" /> Back</button>
                  <button onClick={() => transition(() => setMilestone(3))} data-testid="wiz-team-continue" className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-6 py-3 text-sm rounded-full hover:bg-[#2a3644]">
                    {team.length ? "Continue" : "Skip for now"} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {milestone === 3 && (
              <div data-testid="wiz-modules-step">
                <div className="font-mono text-[10px] tracking-[0.16em] text-[#3E8F99] uppercase mb-4">Integrations</div>
                <h1 className="text-3xl md:text-4xl font-light text-[#1C2530] leading-tight mb-3">Choose your modules</h1>
                <p className="text-base text-[#1C2530]/55 mb-8 max-w-xl">Turn on what your team needs. Disabled modules are fully locked — their screens and APIs won't respond until switched on. Core CRM is always on.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {state.modules.map((m) => {
                    const on = m.core || mods.includes(m.key);
                    return (
                      <button key={m.key} onClick={() => !m.core && toggleMod(m.key)} data-testid={`wiz-module-${m.key}`} disabled={m.core}
                        className={`flex items-center justify-between px-4 py-3.5 border rounded-md text-left transition-all ${on ? "border-[#3E8F99] bg-[#3E8F99]/8" : "border-[#DFDAD0] bg-white hover:border-[#1C2530]/40"} ${m.core ? "opacity-90 cursor-default" : ""}`}>
                        <span className="text-sm font-medium text-[#1C2530]">{m.label}{m.core && <span className="ml-2 font-mono text-[9px] uppercase text-[#1C2530]/40">always on</span>}</span>
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center ${on ? "bg-[#3E8F99] text-white" : "border border-[#DFDAD0]"}`}>{on && <Check className="w-3 h-3" />}</span>
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => nav("/settings")} data-testid="wiz-connect-mailbox" className="mt-5 w-full flex items-center justify-between px-4 py-3 border border-dashed border-[#DFDAD0] rounded-md text-left hover:border-[#3E8F99] transition-colors">
                  <span className="text-sm text-[#1C2530]/75 flex items-center gap-2"><Mailbox className="w-4 h-4 text-[#3E8F99]" /> Connect your mailbox (Gmail / Outlook) to send from your own inbox</span>
                  <span className="font-mono text-[10px] uppercase text-[#3E8F99] shrink-0 ml-3">Open Settings →</span>
                </button>
                <div className="flex items-center gap-3 mt-10">
                  <button onClick={() => transition(() => setMilestone(2))} data-testid="wiz-back" className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#1C2530]/60 hover:text-[#1C2530]"><ArrowLeft className="w-4 h-4" /> Back</button>
                  <button onClick={saveModules} data-testid="wiz-modules-continue" disabled={saving} className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-6 py-3 text-sm rounded-full hover:bg-[#2a3644] disabled:opacity-40">{saving ? "Saving…" : <>Continue <ArrowRight className="w-4 h-4" /></>}</button>
                </div>
              </div>
            )}

            {milestone === 4 && (
              <div data-testid="wiz-review-step">
                <div className="font-mono text-[10px] tracking-[0.16em] text-[#3E8F99] uppercase mb-4">Review & Launch</div>
                <h1 className="text-3xl md:text-4xl font-light text-[#1C2530] leading-tight mb-3">You're all set{review?.w?.business_profile?.company_name ? `, ${review.w.business_profile.company_name}` : ""}</h1>
                <p className="text-base text-[#1C2530]/55 mb-8 max-w-xl">This is exactly what we saved — pulled from your workspace, not this screen. Check it reads right, or jump back to edit. Launching won't change your subscription.</p>
                {!review ? <Loading /> : (() => {
                  const bp = review.w.business_profile || {};
                  const sp = review.w.sales_process || {};
                  const pipe = review.pipe || { labels: {}, approval_stages: [] };
                  const Sec = ({ title, onEdit, testid, children }) => (
                    <div className="bg-white border border-[#DFDAD0] rounded-sm mb-4" data-testid={testid}>
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#DFDAD0]">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">{title}</span>
                        <button onClick={onEdit} data-testid={`${testid}-edit`} className="font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline">Edit</button>
                      </div>
                      <div className="divide-y divide-[#F0EDE6]">{children}</div>
                    </div>
                  );
                  const Row = ({ k, v }) => (
                    <div className="flex gap-4 px-4 py-2.5">
                      <div className="w-44 shrink-0 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/45 pt-0.5">{k}</div>
                      <div className="text-sm text-[#1C2530]/80 flex-1 whitespace-pre-wrap">{(v === 0 || v) ? String(v) : "—"}</div>
                    </div>
                  );
                  return (
                    <div>
                      <Sec title="Business profile" testid="review-profile" onEdit={() => editJump(0)}>
                        <Row k="Company" v={bp.company_name} />
                        <Row k="Workspace name" v={review.w.tenant?.name} />
                        <Row k="Website" v={bp.website} />
                        <Row k="Business model" v={bp.business_model} />
                        <Row k="Deal size" v={bp.budget_range} />
                        <Row k="Reporting currency" v={review.w.currency?.reporting_currency} />
                        <Row k="Also transacts in" v={(review.w.currency?.transact_currencies || []).join(", ") || "Single currency"} />
                        {Object.keys(review.w.currency?.region_currency_defaults || {}).length > 0 && (
                          <Row k="Region defaults" v={Object.entries(review.w.currency.region_currency_defaults).map(([r, c]) => `${r} → ${c}`).join(", ")} />
                        )}
                        <Row k="Sales cycle" v={bp.sales_cycle} />
                        <Row k="Compliance" v={bp.compliance_context} />
                        <Row k="Regulated" v={bp.regulated ? "Yes — legal-review gate on" : "No"} />
                        <Row k="Seats needed" v={bp.seat_estimate} />
                      </Sec>
                      <Sec title="Sales process" testid="review-process" onEdit={() => editJump(1)}>
                        <Row k="Current CRM" v={sp.current_crm} />
                        <Row k="WhatsApp today" v={sp.current_whatsapp_tool} />
                        <Row k="Database size" v={sp.database_size} />
                        <Row k="Needs approval" v={sp.approval_thresholds} />
                        <Row k="Leadership reviews" v={sp.leadership_reviews} />
                        <Row k="Team structure" v={sp.team_structure} />
                        <Row k="Biggest frustration" v={sp.biggest_frustration} />
                      </Sec>
                      <Sec title="Stage names — as they'll appear in Settings → Pipeline" testid="review-stages" onEdit={() => editJump(1, { stage: true })}>
                        {STAGES.map((s) => {
                          const label = pipe.labels?.[s] || s;
                          const custom = label !== s;
                          const needsApproval = (pipe.approval_stages || []).includes(s);
                          return (
                            <div key={s} className="flex items-center gap-4 px-4 py-2" data-testid={`review-stage-${s.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                              <div className="text-sm text-[#1C2530] flex-1"><span className="font-medium">{label}</span>{custom && <span className="ml-2 font-mono text-[9px] uppercase text-[#1C2530]/40">{s}</span>}</div>
                              {needsApproval && <span className="font-mono text-[9px] uppercase text-[#E2711D]">needs approval</span>}
                            </div>
                          );
                        })}
                      </Sec>
                      <Sec title={`Team (${review.users.length})`} testid="review-team" onEdit={() => editJump(2)}>
                        {review.users.length === 0 ? <Row k="Members" v="Just you for now" /> :
                          review.users.map((u) => (
                            <div key={u.id} className="flex items-center gap-3 px-4 py-2 text-sm text-[#1C2530]/80" data-testid={`review-user-${u.id}`}>
                              <Check className="w-3.5 h-3.5 text-[#3E8F99]" /> {u.name} · {u.email} · {u.role}
                            </div>
                          ))}
                      </Sec>
                      <Sec title="Modules on" testid="review-modules" onEdit={() => editJump(3)}>
                        <Row k="Enabled" v={state.modules.filter((m) => m.core || (review.w.enabled_modules || []).includes(m.key)).map((m) => m.label).join(", ")} />
                      </Sec>
                    </div>
                  );
                })()}
                <div className="flex items-center gap-3 mt-10">
                  <button onClick={() => transition(() => setMilestone(3))} data-testid="wiz-back" className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#1C2530]/60 hover:text-[#1C2530]"><ArrowLeft className="w-4 h-4" /> Back</button>
                  <button onClick={launch} data-testid="wiz-launch" disabled={saving} className="flex items-center gap-2 bg-[#E50914] text-white px-7 py-3 text-sm rounded-full hover:bg-[#c40810] disabled:opacity-40 shadow-[0_0_20px_rgba(229,9,20,0.35)]">
                    <Rocket className="w-4 h-4" /> {saving ? "Finishing…" : "Launch workspace"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 md:px-16 py-5 flex justify-end">
          <button onClick={() => { saveDraft(); nav("/"); }} data-testid="wiz-exit" className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/40 hover:text-[#1C2530]/70 flex items-center gap-1"><X className="w-3 h-3" /> Save & exit</button>
        </div>
      </main>
    </div>
  );
}
